import {
  ChevronRightIcon,
  DragIndicatorIcon,
  TigaCreateDerivativeIcon,
} from '@fmlx/icons';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import BatchLoadExperimentWizardContext, {
  EBatchloadDirectoryType,
} from './BatchLoadExperimentWizardContext';

const DIRECTORY_TYPE_LABELS: Record<EBatchloadDirectoryType, string> = {
  [EBatchloadDirectoryType.FOLDER]: 'Folder',
  [EBatchloadDirectoryType.SUBFOLDER]: 'Sub Folder',
  [EBatchloadDirectoryType.PROJECT]: 'Project',
};

export const FolderHierarchyEditor = () => {
  const batchLoadWizardContext = useContext(BatchLoadExperimentWizardContext);
  const dragItemIndex = useRef<number | null>(null);
  const [folderNames, setFolderNames] = useState<{
    folder?: string;
    subFolder?: string;
    project?: string;
  }>({
    folder: 'Folder',
    subFolder: 'Sub Folder',
    project: 'Project',
  });

  useEffect(() => {
    if (
      batchLoadWizardContext.batchLoadExperimentsDTO?.batchLoadExperimentsData?.length > 0
    ) {
      const firstRow =
        batchLoadWizardContext.batchLoadExperimentsDTO?.batchLoadExperimentsData?.[0];
      setFolderNames({
        folder: firstRow?.folder?.value || 'Folder',
        subFolder: firstRow?.subFolder?.value || 'Sub Folder',
        project: firstRow?.project?.value || 'Project',
      });
    } else {
      setFolderNames({ folder: 'Folder', subFolder: 'Sub Folder', project: 'Project' });
    }
  }, [batchLoadWizardContext.batchLoadExperimentsDTO]);

  const getHierarchyDirName = (dirType: EBatchloadDirectoryType) => {
    switch (dirType) {
      case EBatchloadDirectoryType.FOLDER:
        return folderNames.folder ?? 'Folder';
      case EBatchloadDirectoryType.SUBFOLDER:
        return folderNames.subFolder ?? 'Sub Folder';
      case EBatchloadDirectoryType.PROJECT:
        return folderNames.project ?? 'Project';
      default:
        return '';
    }
  };

  const onFolderHierarchyDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    index: number,
  ) => {
    dragItemIndex.current = index;
    e.dataTransfer.setDragImage(
      e.currentTarget,
      e.currentTarget.offsetWidth / 2,
      e.currentTarget.offsetHeight / 2,
    );
  };

  const onFolderHierarchyDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const onFolderHierarchyDrop = (index: number) => {
    if (dragItemIndex.current === null || dragItemIndex.current === index) {
      return;
    }
    const newOrder = [...batchLoadWizardContext.folderHierarchyOrder];
    const [draggedItem] = newOrder.splice(dragItemIndex.current, 1);
    newOrder.splice(index, 0, draggedItem);
    batchLoadWizardContext.setFolderHierarchyOrder(newOrder);
    dragItemIndex.current = null;
  };

  const folderHierarchyOrder = useMemo<string>(() => {
    return batchLoadWizardContext.folderHierarchyOrder
      .map((item) => getHierarchyDirName(item) ?? item)
      .join(' / ');
  }, [batchLoadWizardContext.folderHierarchyOrder, getHierarchyDirName]);

  return (
    <div
      id="folderhierarchyeditor-wrapper"
      className="flex flex-col py-1 px-4 justify-center bg-[#FAFAFA] rounded-md"
    >
      <div
        id="folderhierarchyeditor-option-row"
        className="flex flex-row items-center justify-center gap-2"
      >
        <TigaCreateDerivativeIcon customColor="#F7A246" />
        <span id="folderhierarchyeditor-option-title" className="font-medium shrink-0">
          Hierarchy Option :
        </span>
        {batchLoadWizardContext.folderHierarchyOrder.map((item, index) => {
          const isSubFolder = item === EBatchloadDirectoryType.SUBFOLDER;

          return (
            <React.Fragment key={item}>
              {index > 0 && <ChevronRightIcon customColor="#F7A246" />}
              <div
                id={`folderhierarchyeditor-card-${item}`}
                draggable={!isSubFolder}
                onDragStart={
                  isSubFolder ? undefined : (e) => onFolderHierarchyDragStart(e, index)
                }
                onDragOver={isSubFolder ? undefined : onFolderHierarchyDragOver}
                onDrop={isSubFolder ? undefined : () => onFolderHierarchyDrop(index)}
                className={`flex w-full items-center gap-2 my-2 p-2 border border-solid border-[#CACACA] rounded-md bg-white select-none ${
                  isSubFolder ? '' : 'cursor-grab'
                }`}
              >
                {!isSubFolder && <DragIndicatorIcon customColor="#888888" />}
                <TigaCreateDerivativeIcon customColor="#F7A246" />
                <span className="font-medium">{DIRECTORY_TYPE_LABELS[item] ?? item}</span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
      <div id="folderhierarchyeditor-divider" className="h-px -mx-4 bg-[#E0E0E0]" />
      <div
        id="folderhierarchyeditor-generatedpath-row"
        className="flex flex-row items-center gap-2 p-2"
      >
        <span
          id="folderhierarchyeditor-generatedpath-title"
          className="font-medium text-sm"
        >
          Generated Path :
        </span>
        <span id="folderhierarchyeditor-generatedpath-value" className="text-sm">
          {folderHierarchyOrder}
        </span>
      </div>
    </div>
  );
};
